/* This service maintains and manages user impersonation state.
 * It is primarily managed in the UserManagementService.
 */

import { Injectable } from '@angular/core';
import { UserService } from './user.service';
import { LimegearUserModel, LimegearUserImpersonationModel } from '../classes/limegearUser.model';
import { ToastService } from './toast.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { CurrentUserStateService } from './current-user-state.service';

@Injectable({
    providedIn: 'root',
})
export class ImpersonationService {
    impersonateClient: string;
    impersonateUser: string;
    impersonateUserName: string;
    private isImpersonatePowerUser: boolean;
    private isImpersonateClientAdmin: boolean;
    private isImpersonateHasGLAccess: boolean;

    private serverUrl = 'https://' + window.location.host;
    private version = '0.5';
    private _url = (this.serverUrl + '/api/v{version}/userimpersonation').replace(
        '{version}',
        this.version
    );

    public impersonatingUserSubject = new BehaviorSubject(false);
    public impersonatingClientSubject = new BehaviorSubject(false);
    constructor(
        private toastService: ToastService,
        private http: HttpClient,
        private currentUserStateService: CurrentUserStateService,
        private userService: UserService
    ) {
        if (this.isImpersonatingUser()) {
            this.impersonatingUserSubject.next(true);
        }
    }

    public initImpersonation() {
        if (this.currentUserStateService.isLoggedIn()) {
            if (
                !this.isImpersonating() &&
                (this.currentUserStateService.isLBASuperUser() ||
                    this.currentUserStateService.isPlatformUser() ||
                    this.currentUserStateService.isDemoUser())
            ) {
                // Set default impersonation client to BigHome for Super, Platform, and Demo users
                let clientShortName = localStorage.getItem('impersonateClient') ?? 'Big Home';
                this.startImpersonatingClient(clientShortName);
            }
        }
    }

    // Returns true if any impersonation data exists (do not call this method directly. Use 'isImpersonatingUser' or 'isImpersonatingClient')
    public isImpersonating(): boolean {
        return (
            this.currentUserStateService.isLoggedIn() &&
            ((this.impersonateUser != null && this.impersonateUser != undefined) ||
                (this.impersonateClient != null && this.impersonateClient != undefined))
        );
    }

    // Returns true if user is impersonating another user
    public isImpersonatingUser(): boolean {
        return (
            this.isImpersonating() &&
            this.impersonateUser != null &&
            this.impersonateUser != undefined
        );
    }

    public isImpersonatingPowerUser(): boolean {
        return this.isImpersonatePowerUser;
    }

    public isImpersonatingClientAdmin(): boolean {
        return this.isImpersonateClientAdmin;
    }

    public isImpersonatingWithGLAccess(): boolean {
        return this.isImpersonateHasGLAccess;
    }

    // Returns true if user is 'impersonating' a c (Only SimpleNexus Admins)
    public isImpersonatingClient(): boolean {
        return (
            this.isImpersonating() &&
            this.impersonateClient !== null &&
            this.impersonateClient !== undefined
        );
    }

    // Handles starting a client impersonation session
    public startImpersonatingClient(clientShortName: string) {
        let impersonation: LimegearUserImpersonationModel = {
            loginEmail: null,
            clientShortName: clientShortName,
        };

        this.setImpersonationClient(impersonation);
        this.toastService.info(`Changed client to ${clientShortName}`, {
            timeout: 5000,
        });
    }

    // Handles starting a user impersonation session
    public startImpersonatingUser(loginEmail: string, refresh: boolean = false) {
        let impersonation: LimegearUserImpersonationModel = {
            loginEmail: loginEmail,
            clientShortName: this.impersonateClient,
        };

        //client refreshed the screen so we do not need to log a new session
        if (refresh) {
            this.setImpersonationUser(impersonation);
            this.impersonatingUserSubject.next(true);
        } else {
            this.startImpersonationServiceRequest(loginEmail).subscribe(
                () => {
                    this.setImpersonationUser(impersonation);
                    this.impersonatingUserSubject.next(true); // Alert other services that user is impersonating
                },
                (error) => {
                    throw new Error(error);
                }
            );
        }
    }

    // Handles ending a user impersonation session.
    // Triggers the 'impersonationUserSubject' to alert other services that the session has ended (UserManagementService).
    // Called by UserManagementService when the user clicks the 'End' button on the toast.
    public stopImpersonatingUser() {
        if (this.impersonateUser == null) return;

        let impersonation: LimegearUserImpersonationModel = {
            loginEmail: null,
            clientShortName: this.impersonateClient,
        };

        this.endImpersonationServiceRequest(this.impersonateUser).subscribe({
            error: () => {
                // Ignore error as it is being logged and managed on the API
            },
        });

        this.impersonateUser = null;
        this.setImpersonationData(impersonation);
        this.impersonatingUserSubject.next(false); // Alert other services that user is no longer impersonating
    }

    // Handles ending client impersonation session.
    public stopImpersonatingClient() {
        let impersonation: LimegearUserImpersonationModel = null;

        this.impersonateClient = null;
        this.impersonateUser = null;
        this.setImpersonationData(impersonation);
    }

    // Updates impersonation session data and tells the UserService to update impersonation local storage value
    private setImpersonationData(impersonation: LimegearUserImpersonationModel) {
        this.impersonateUser = impersonation?.loginEmail;
        if (impersonation?.loginEmail) {
            localStorage.setItem('impersonateUser', impersonation?.loginEmail);
        } else {
            localStorage.removeItem('impersonateUser');
        }
        this.impersonateClient = impersonation?.clientShortName;
        if (impersonation?.clientShortName) {
            localStorage.setItem('impersonateClient', impersonation?.clientShortName);
        } else {
            localStorage.removeItem('impersonateClient');
        }
    }

    private setImpersonationClient(impersonation: LimegearUserImpersonationModel) {
        this.impersonateClient = impersonation.clientShortName;
        if (impersonation?.clientShortName) {
            localStorage.setItem('impersonateClient', impersonation?.clientShortName);
        } else {
            localStorage.removeItem('impersonateClient');
        }

        this.impersonatingClientSubject.next(true);
    }

    private setImpersonationUser(impersonation: LimegearUserImpersonationModel) {
        this.impersonateUser = impersonation.loginEmail;
        if (impersonation?.loginEmail) {
            localStorage.setItem('impersonateUser', impersonation?.loginEmail);
            this.userService.getUserByEmail(this.impersonateUser).subscribe(
                (data) => {
                    this.isImpersonatePowerUser = data.isClientAdmin;
                    this.isImpersonateClientAdmin = data.isClientOwner;
                    this.isImpersonateHasGLAccess = data.hasGLAccess;
                },
                (error) => {
                    throw error;
                }
            );
        } else {
            localStorage.removeItem('impersonateUser');
        }
    }

    // Tells the ToastService to create an info toast with impersonation logic.
    public openImpersonationToast() {
        this.toastService.info(`Viewing as ${this.impersonateUser}`, {
            dismiss: true,
            timeout: 0,
            actionText: 'End',
            logic: 'impersonation',
        });
    }

    // HTTP Requests
    private startImpersonationServiceRequest(impersonatedLoginEmail: string): Observable<any> {
        return this.http.put(this._url, {
            impersonatedLoginEmail: impersonatedLoginEmail,
        });
    }

    private endImpersonationServiceRequest(impersonatedLoginEmail: string): Observable<any> {
        return this.http.post(this._url, {
            impersonatedLoginEmail: impersonatedLoginEmail,
        });
    }

    public isPowerUser(): boolean {
        let powerUserCheck: boolean = false;

        if (
            ((this.currentUserStateService.isLoggedIn() &&
                this.currentUserStateService.isPowerUser()) ||
                this.currentUserStateService.isPlatformUser()) &&
            !this.isImpersonatingUser()
        ) {
            powerUserCheck = true;
        } else if (
            this.currentUserStateService.isLoggedIn() &&
            this.isImpersonatingUser() &&
            this.isImpersonatingPowerUser()
        ) {
            powerUserCheck = true;
        } else {
            powerUserCheck = false;
        }

        return powerUserCheck;
    }
}
